/**
 * Turning an Elliott analysis into a chart overlay.
 *
 * Kept out of the click handler so the shape the chart receives is a tested value rather than an
 * object literal assembled inline. `chartDataProcessor` reads `type`, `degree`, `points`, `layers`,
 * `targets` and `projection`; every other field is for the overlay list and the journal.
 *
 * <h2>One overlay, several drawings</h2>
 * A count, its sub-count, its price targets and its forward projection are one statement about the
 * chart and are shown and hidden together. They are therefore ONE overlay carrying several parts,
 * not several overlays — {@code removeOverlay} matches a single exact id, so a set of sibling
 * overlays would need the caller to remember every id it created and remove each. That is a list
 * that goes stale the first time a new part is added.
 */

import {
  degreeLayers, fibTargets, futureProjection, waveSegments, targetCallout, projectionVectors,
  degreeBridges, PROJECTION_COLOR,
} from './elliottProjections';
import { FUTURE_VIEW_BARS } from './technicalDrawings';

/**
 * One id, reused.
 *
 * A wave overlay is a statement about the chart currently loaded, so a second one is never wanted
 * — re-running the analysis or changing symbol replaces it. A fresh id per run would stack counts
 * from different symbols on one chart, each looking like a count of what is now displayed.
 */
export const ELLIOTT_OVERLAY_ID = 'elliott-wave-primary';

/**
 * Bars the targets and the projection run past the count's last pivot.
 *
 * The chart's whole visible future window, because that is what these are for: a target the reader
 * cannot see is not a target. Sized against the same constant the drawing tools use, so nothing on
 * this chart projects past the space the chart actually shows.
 */
export const ELLIOTT_PROJECTION_BARS = FUTURE_VIEW_BARS;

/**
 * The overlay for a result's counts, or null when there is nothing to draw.
 *
 * Returns null rather than an empty overlay so the caller can leave the switch disabled instead of
 * turning it on and putting nothing on the chart.
 */
export function buildElliottOverlay(result, { color = null, anchorIndex, verified } = {}) {
  const overlay = result?.primaryConclusion?.waveOverlay;
  const points = overlay?.points;
  if (!Array.isArray(points) || points.length < 2) return null;

  // Points whose bar index or price cannot be read are dropped here as well as in the renderer:
  // two pivots are needed for a line, and it is better to know that now than to add an overlay
  // that silently renders nothing.
  const usable = points.filter(p =>
    p && Number.isInteger(p.barIndex) && typeof p.price === 'number' && Number.isFinite(p.price));
  if (usable.length < 2) return null;

  // Coarsest first, one per degree. Falls back to the primary alone, so an engine that returned
  // counts at a single degree still draws — see degreeLayers for why this is not just top3.
  // Anchored: a count that stopped describing the market long before the clicked bar is dropped
  // here rather than drawn beside a current one. Passing the anchor is what turns the engine's
  // pivot-counted currency test into a bar-counted one - see currentLayers.
  const layers = degreeLayers(result, anchorIndex);
  const drawn = layers.length > 0
    ? layers
    : [{ degree: overlay.degree, points: usable, pattern: result.primaryConclusion.pattern }];

  // Targets and the projection come from the FINEST layer, which is the one whose last pivot is
  // most recent. A coarse count's "wave 2 complete" may be months old; projecting from it would
  // draw a target zone for a move that has already happened.
  const finest = drawn[drawn.length - 1];
  const horizon = finest.points[finest.points.length - 1].barIndex + ELLIOTT_PROJECTION_BARS;

  // Hoisted out of the returned literal so the drawn extent below can measure them. They are what
  // reaches furthest right, and the viewport has to know that before it frames anything.
  const targets = fibTargets(finest).map(t => ({ ...t, callout: targetCallout(t) }));
  const projection = futureProjection(finest, horizon);

  // Every standing projection as a drawable vector, coloured by what became of it. Distinct from
  // `projection` above, which is one dashed segment towards the nearest target of the finest layer
  // only - it says where the count points, and these say how far, how long, and whether it worked.
  const vectors = projectionVectors(verified || []);

  // The unlabelled stretches between one count and the next, so the sequence reads as continuous
  // across a degree transition without claiming a leg nobody labelled.
  const bridges = degreeBridges(drawn);

  return {
    type: 'elliottWave',
    id: ELLIOTT_OVERLAY_ID,
    // Kept alongside `layers` rather than replaced by it: the overlay list and the journal read
    // these, and the renderer still accepts a single-layer overlay from an older caller.
    degree: overlay.degree,
    points: usable,
    // Each layer carries its own leg annotations, computed here so the renderer stays a drawing
    // step and the text is assertable without rendering anything.
    layers: drawn.map(layer => ({ ...layer, segments: waveSegments(layer) })),
    projectionBars: ELLIOTT_PROJECTION_BARS,
    targets,
    projection,
    projections: vectors,
    bridges,
    // The pen the targets and the projections are drawn in. On the overlay rather than imported by
    // the renderer so an overlay carries its own appearance, the way `color` already does.
    projectionColor: PROJECTION_COLOR,
    label: `Elliott · ${result.primaryConclusion.pattern} · ${result.primaryConclusion.position}`,
    symbol: result.symbol,

    // The user's chosen line colour, when there is one.
    //
    // Absent, every layer keeps its degree's own pen, which is the point of the degree ladder — the
    // colour IS the degree. Present, it overrides all of them, because a user who has picked a
    // colour has said something more specific than the ladder's default and the study must honour
    // it. The degrees stay distinguishable by width, dash and label notation, all of which survive
    // the override.
    color: color || null,

    // The span the count actually covers, so a caller can put it on screen.
    //
    // Not cosmetic. The engine returns only CURRENT patterns (maxPivotsBehind = 2), and on a long
    // series those are routinely a handful of bars at the extreme right: a real AAPL run gave three
    // counts spanning 2, 2 and 4 bars out of 11,502. Rendered at full-history zoom that is under a
    // pixel, which is indistinguishable from the overlay not working at all — and was reported as
    // exactly that.
    firstBarIndex: Math.min(...drawn.flatMap(l => l.points.map(pt => pt.barIndex))),
    lastBarIndex: Math.max(...drawn.flatMap(l => l.points.map(pt => pt.barIndex))),

    // The rightmost bar ANYTHING in this overlay reaches, which is not the last pivot.
    //
    // The targets and the projection run into future space past the count — that is their whole
    // purpose — so a viewport framed on the pivots alone crops them out. That is exactly what
    // happened: the focus window ended at the last pivot plus padding while the projection ran
    // fifty bars further, and the dotted leg was drawn, correct, and off-screen. Reported as "I
    // still do not see a dotted trend segment", which it was not, because it was never in view.
    drawnLastIndex: Math.max(
      ...drawn.flatMap(l => l.points.map(pt => pt.barIndex)),
      ...(projection ? [projection.to.index] : []),
      ...targets.map(t => t.fromIndex + ELLIOTT_PROJECTION_BARS),
      // The vectors reach further than anything else when a projection is a long one, and a
      // viewport framed without them crops off the very thing the reader asked to see.
      ...vectors.flatMap(v => [v.to.index, ...(v.reachedAt ? [v.reachedAt.index] : [])]),
    ),
  };
}
